import { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, AlertTriangle, Mic, ChevronRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-1 — SugestaoNexoVisaoCard
// Sugestão proativa derivada do briefing focada em finance:
//   1. finance_alerts[0] → "Alerta: {category} está {pct}% acima da
//      média mensal" + CTA voz/navegação
//   2. priority_actions com label envolvendo finance/gasto/saldo →
//      mostra como ação prioritária
//   3. Empty: retorna null (linha 0 some inteira; brief #11)

function pickFinanceContent(briefing) {
  if (!briefing) return null;

  const alerts = briefing.finance_alerts || [];
  if (alerts.length > 0) {
    const a = alerts[0];
    const cat = (a?.category || "categoria").toString();
    const pct = a?.percent_above != null ? `${a.percent_above}%` : "acima";
    const current = a?.current != null
      ? `R$ ${Number(a.current).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`
      : null;
    const avg = a?.average != null
      ? `R$ ${Number(a.average).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`
      : null;
    return {
      kind: "alert",
      key: `alert:${cat}:${pct}`,
      title: `Alerta: ${cat.charAt(0).toUpperCase() + cat.slice(1)} está ${pct} acima da média mensal`,
      description: current && avg
        ? `Atual ${current} vs média histórica ${avg}. Quer ver onde foi parar?`
        : "Categoria fora do padrão histórico. Quer investigar?",
      ctaLabel: "Investigar com o Nexo",
      ctaIcon: Mic,
      voiceQuery: `por que ${cat} foi tanto este mês?`,
    };
  }

  const actions = (briefing.priority_actions || []).filter(a => {
    const label = String(a?.label || a?.command || "").toLowerCase();
    return /finan|gasto|saldo|despes|receit|orçament|fatura|cart[ãa]o/.test(label);
  });
  if (actions.length > 0) {
    const a = actions[0];
    return {
      kind: "action",
      key: `action:${a?.command || a?.label || "0"}`,
      title: a?.label || "Ação prioritária do financeiro",
      description: "O Nexo identificou algo que merece atenção agora.",
      ctaLabel: "Revisar com o Nexo",
      ctaIcon: Mic,
      voiceQuery: a?.command || a?.label,
    };
  }

  return null;
}

export default function SugestaoNexoVisaoCard({ briefing, onTalk }) {
  const content = useMemo(() => pickFinanceContent(briefing), [briefing]);

  if (!content) return null;  // linha 0 some inteira

  const InnerIcon = AlertTriangle;
  const CtaIcon = content.ctaIcon || ChevronRight;

  return (
    <Card
      variant="elevated"
      className="relative overflow-hidden glass-card-strong gradient-border-cyan glow-hover p-6 md:p-8"
      data-testid="finance-sugestao-card"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 0% 0%, rgba(0,212,255,0.06), transparent 45%)" }}
      />

      <div className="relative">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg bg-brand-cyan/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-brand-cyan" aria-hidden="true" />
          </div>
          <span
            className="text-caption font-semibold uppercase text-brand-cyan/80"
            style={{ letterSpacing: "0.12em" }}
          >
            Sugestão do Nexo
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={content.key}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="space-y-3"
          >
            {content.kind === "alert" && (
              <div className="inline-flex w-8 h-8 rounded-md items-center justify-center bg-status-warning/10 text-status-warning">
                <InnerIcon className="w-4 h-4" aria-hidden="true" />
              </div>
            )}
            <h3 className="font-display font-semibold text-foreground text-h2 max-w-2xl">
              {content.title}
            </h3>
            <p className="text-body text-foreground-muted max-w-2xl">
              {content.description}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="relative mt-6">
        <Button
          variant="primary-tech"
          size="md"
          onClick={() => onTalk?.(content.voiceQuery)}
          data-testid="finance-sugestao-cta"
        >
          <CtaIcon className="w-4 h-4" />
          {content.ctaLabel}
        </Button>
      </div>
    </Card>
  );
}
